import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { getDataSource } from "@/lib/data-source";
import { supabase } from "@/integrations/supabase/client";
import { JOB_STATUSES, type Job } from "@/lib/types";
import { StatusChip } from "@/components/StatusChip";
import { format, isToday, isBefore, startOfDay } from "date-fns";
import {
  AlertTriangle,
  Clock,
  Plus,
  ArrowRight,
  KanbanSquare,
  Table2,
  CheckCircle2,
  Layers,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FavouriteButton } from "@/components/FavouriteButton";
import { listFavouriteJobIds } from "@/lib/favourites";
import { QuickActionsPanel } from "@/components/staff/QuickActionsPanel";

export const Route = createFileRoute("/app/staff/today")({ component: TodayDashboard });

function TodayDashboard() {
  const { profile } = useAuth();
  const { demoMode } = useDemoMode();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );

  const { data: jobs = [] } = useQuery({
    queryKey: ["staff-jobs", demoMode],
    queryFn: () => ds.listJobs(),
  });

  const { data: favIds = [] } = useQuery({
    queryKey: ["favourites", profile?.id],
    queryFn: () => (profile?.id ? listFavouriteJobIds(profile.id) : Promise.resolve([])),
    enabled: !!profile?.id,
  });
  const favouriteJobs = useMemo(() => jobs.filter((j) => favIds.includes(j.id)), [jobs, favIds]);

  const { data: unreadCount } = useQuery({
    queryKey: ["unread-count", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", profile.id)
        .is("read_at", null)
        .is("is_read", false);
      return count || 0;
    },
  });

  // Get preference for "My jobs only"
  const myJobsOnly = useMemo(() => {
    try {
      return localStorage.getItem("flightdeck_myJobsOnly") === "true";
    } catch {
      return false;
    }
  }, []);

  // Filter and categorize jobs
  const categorizedJobs = useMemo(() => {
    const today = startOfDay(new Date());
    const filtered = jobs.filter((j) => {
      if (myJobsOnly && j.assignee_profile_id !== profile?.id) return false;
      return true;
    });

    const dueToday = filtered.filter((j) => {
      if (!j.due_date) return false;
      const dueDate = startOfDay(new Date(j.due_date));
      return isToday(dueDate) && j.status !== "Completed";
    });

    const newJobs = filtered.filter((j) => {
      const createdDate = new Date(j.created_at);
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return createdDate >= oneDayAgo && j.status === "New";
    });

    const onHold = filtered.filter((j) => j.status === "On Hold");

    const overdue = filtered.filter((j) => {
      if (!j.due_date || j.status === "Completed") return false;
      return isBefore(startOfDay(new Date(j.due_date)), today);
    });

    const active = filtered.filter((j) => j.status !== "Completed");

    return { dueToday, newJobs, onHold, overdue, active, total: filtered.length };
  }, [jobs, myJobsOnly, profile?.id]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="rounded-3xl bg-gradient-hero p-6 text-primary-foreground shadow-pop">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium opacity-80">
              {format(new Date(), "EEEE, d MMMM yyyy")}
            </p>
            <h1 className="mt-1 text-3xl font-bold">
              {greeting}
              {profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-1 text-sm opacity-75">
              {categorizedJobs.active.length} active jobs ·{" "}
              {categorizedJobs.onHold.length > 0
                ? `${categorizedJobs.onHold.length} on hold`
                : "nothing on hold"}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link to="/app/staff/jobs-board">
              <Button
                size="sm"
                variant="secondary"
                className="rounded-full gap-1.5 bg-white/20 text-primary-foreground border-0 hover:bg-white/30"
              >
                <KanbanSquare className="h-3.5 w-3.5" /> Board
              </Button>
            </Link>
            <Link to="/app/staff/jobs-table" search={{ filter: undefined }}>
              <Button
                size="sm"
                variant="secondary"
                className="rounded-full gap-1.5 bg-white/20 text-primary-foreground border-0 hover:bg-white/30"
              >
                <Table2 className="h-3.5 w-3.5" /> Table
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <QuickActionsPanel />

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          label="Active jobs"
          value={categorizedJobs.active.length}
          icon={Layers}
          to="/app/staff/jobs-board"
        />
        <StatCard
          label="Due today"
          value={categorizedJobs.dueToday.length}
          icon={Clock}
          highlight={categorizedJobs.dueToday.length > 0}
          to="/app/staff/jobs-table"
          tableFilter="due-today"
        />
        <StatCard
          label="Overdue"
          value={categorizedJobs.overdue.length}
          icon={AlertTriangle}
          urgent={categorizedJobs.overdue.length > 0}
          to="/app/staff/jobs-table"
          tableFilter="overdue"
        />
        <StatCard
          label="On hold"
          value={categorizedJobs.onHold.length}
          icon={AlertTriangle}
          urgent={categorizedJobs.onHold.length > 0}
          to="/app/staff/jobs-board"
        />
      </div>

      {unreadCount && unreadCount > 0 ? (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary font-medium">
          You have {unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}.
        </div>
      ) : null}

      {/* Favourites section */}
      {favouriteJobs.length > 0 && (
        <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-6 shadow-soft">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-5 w-5 fill-current text-amber-500" />
            <h2 className="text-lg font-semibold">Favourites</h2>
            <span className="ml-2 text-sm text-muted-foreground">({favouriteJobs.length})</span>
          </div>
          <div className="space-y-3">
            {favouriteJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </div>
      )}

      {/* Due today section */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold">Due today</h2>
          <span className="ml-2 text-sm text-muted-foreground">
            ({categorizedJobs.dueToday.length})
          </span>
        </div>
        {categorizedJobs.dueToday.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No jobs due today</div>
        ) : (
          <div className="space-y-3">
            {categorizedJobs.dueToday.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>

      {/* New jobs section */}
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold">New jobs (last 24h)</h2>
          <span className="ml-2 text-sm text-muted-foreground">
            ({categorizedJobs.newJobs.length})
          </span>
        </div>
        {categorizedJobs.newJobs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No new jobs</div>
        ) : (
          <div className="space-y-3">
            {categorizedJobs.newJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>

      {/* On hold section */}
      <div className="rounded-3xl border border-amber-500/30 bg-amber-500/5 p-6 shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-amber-900">Needs attention</h2>
          <span className="ml-2 text-sm text-amber-700">({categorizedJobs.onHold.length})</span>
        </div>
        {categorizedJobs.onHold.length === 0 ? (
          <div className="py-8 text-center text-sm text-amber-700">No jobs on hold</div>
        ) : (
          <div className="space-y-3">
            {categorizedJobs.onHold.map((job) => (
              <JobCard key={job.id} job={job} highlight />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  highlight,
  urgent,
  to,
  tableFilter,
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  highlight?: boolean;
  urgent?: boolean;
  to: string;
  tableFilter?: string;
}) {
  const nav = useNavigate();

  const handleClick = () => {
    if (to === "/app/staff/jobs-table") {
      nav({ to: "/app/staff/jobs-table", search: { filter: tableFilter ?? undefined } });
    } else {
      nav({ to: to as "/app/staff/jobs-board" });
    }
  };

  return (
    <div onClick={handleClick} className="cursor-pointer">
      <div
        className={cn(
          "rounded-2xl border bg-card p-4 shadow-soft transition-all hover:shadow-md",
          urgent && value > 0
            ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20"
            : highlight && value > 0
              ? "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20"
              : "border-border",
        )}
      >
        <div className="flex items-center justify-between mb-2">
          <Icon
            className={cn(
              "h-4 w-4",
              urgent && value > 0
                ? "text-red-500"
                : highlight && value > 0
                  ? "text-amber-500"
                  : "text-muted-foreground",
            )}
          />
          <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
        </div>
        <div
          className={cn(
            "text-2xl font-bold",
            urgent && value > 0
              ? "text-red-600 dark:text-red-400"
              : highlight && value > 0
                ? "text-amber-600 dark:text-amber-400"
                : "text-foreground",
          )}
        >
          {value}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function JobCard({ job, highlight }: { job: Job; highlight?: boolean }) {
  const dueDate = job.due_date ? new Date(job.due_date) : null;
  const isOverdue =
    dueDate && isBefore(dueDate, startOfDay(new Date())) && job.status !== "Completed";

  return (
    <div className="relative">
      <Link to="/app/staff/jobs/$id" params={{ id: job.id }} className="block">
        <div
          className={cn(
            "rounded-2xl border border-border bg-gradient-soft p-4 transition-colors hover:bg-muted/50",
            highlight && "border-amber-500/50 bg-amber-500/10",
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-muted-foreground">
                  #{job.id.slice(0, 8)}
                </span>
                <StatusChip status={job.status} />
              </div>
              <div className="font-medium truncate">{job.job_title}</div>
              <div className="text-sm text-muted-foreground">
                {job.customer?.company_name || "Unknown customer"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 pr-8">
              {dueDate && (
                <div
                  className={cn(
                    "text-xs px-2 py-1 rounded-full font-medium",
                    isOverdue ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground",
                  )}
                >
                  {isOverdue
                    ? `Overdue by ${Math.floor((Date.now() - dueDate.getTime()) / (1000 * 60 * 60 * 24))} days`
                    : "Today"}
                </div>
              )}
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      </Link>
      <div className="absolute right-2 top-2">
        <FavouriteButton jobId={job.id} size="sm" />
      </div>
    </div>
  );
}
